import type { SubmissionField } from './engine';
import type { FetchLike } from './transport';

/**
 * The one path a reader's own words are allowed to take.
 *
 * An `answer` event carries `value_text_ref` and a `submit` event carries `submission_ref`, both
 * ids of a record somewhere else, and the event schema rejects anything that tries to carry the
 * text itself. So a survey or form posts its values here first, gets references back, and only
 * then sends events carrying those references. There is no path in this widget that puts a typed
 * value into an event, and there must never be one.
 *
 * Nothing here logs a value either. The only thing this module ever hands back is a set of ids.
 */

const SUBMISSION_REF = /^sub_[0-9a-f]{32}$/;
const RESPONSE_REF = /^rsp_[0-9a-f]{32}$/;
const QUESTION_ID = /^que_[0-9a-f]{32}$/;
const EVENTS_SUFFIX = '/events';

/**
 * Work the submissions endpoint out from the events one.
 *
 * This is a fallback for documents published before `endpoints.submissions` existed. New documents
 * carry the url and this is not consulted at all. Returns null when the events endpoint is not
 * shaped the way the derivation assumes, which is safer than posting a reader's answers at a
 * guessed url.
 */
export function submissionsUrl(eventsEndpoint: string): string | null {
  if (!eventsEndpoint.endsWith(EVENTS_SUFFIX)) return null;
  return `${eventsEndpoint.slice(0, -EVENTS_SUFFIX.length)}/submissions`;
}

export interface SubmissionRequest {
  readonly interaction_id: string;
  readonly interaction_version: number;
  readonly session_id: string;
  readonly fields: readonly SubmissionField[];
}

export interface SubmissionReceipt {
  /** The whole submission, for the submit event. */
  readonly submission_ref: string;
  /** One response reference per stored field, keyed by question id, for the answer events. */
  readonly refs: ReadonlyMap<string, string>;
}

function readRefs(payload: Record<string, unknown>): Map<string, string> {
  const refs = new Map<string, string>();
  const fields = payload['fields'];
  if (!Array.isArray(fields)) return refs;

  for (const entry of fields) {
    if (typeof entry !== 'object' || entry === null) continue;
    const { question_id: questionId, response_ref: ref } = entry as Record<string, unknown>;
    if (typeof questionId !== 'string' || !QUESTION_ID.test(questionId)) continue;
    if (typeof ref !== 'string' || !RESPONSE_REF.test(ref)) continue;
    refs.set(questionId, ref);
  }
  return refs;
}

/**
 * Post the values and return the references, or null if anything at all went wrong.
 *
 * Null is not a detail the caller can paper over: without a submission reference there is no
 * submit event to send and no answer events for the free text questions, so the reader is told the
 * answers were not saved and offered another go. Losing them quietly would be the worst of the
 * three outcomes.
 *
 * A response that carries the submission reference but no per field references is still usable: a
 * submit event goes out and the free text questions simply have no answer events, which is what
 * the widget did before those references existed.
 */
export async function postSubmission(
  url: string,
  fetchImpl: FetchLike,
  body: SubmissionRequest,
): Promise<SubmissionReceipt | null> {
  try {
    const response = await fetchImpl(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      credentials: 'omit',
      mode: 'cors',
    });
    if (!response.ok) return null;

    const payload = (await response.json()) as unknown;
    if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) return null;

    const record = payload as Record<string, unknown>;
    const submissionRef = record['submission_ref'];
    if (typeof submissionRef !== 'string' || !SUBMISSION_REF.test(submissionRef)) return null;

    return { submission_ref: submissionRef, refs: readRefs(record) };
  } catch {
    return null;
  }
}
